import { NmLoader } from '../NmLoader';
import { WorkoutChromeIconButton } from './WorkoutChromeIconButton';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { IoCheckmarkCircle, IoWatchOutline } from 'react-icons/io5';
import { WorkoutTypeIcon } from '../WorkoutTypeIcon';
import { sportGradient } from '../../utils/sportGradient';

export function GarminSyncIndicator({ label }: { label: string }) {
  return (
    <span
      className="workout-detail-panel__header-sync"
      title={label}
      role="img"
      aria-label={label}
    >
      <IoWatchOutline className="workout-detail-panel__header-sync-watch" aria-hidden />
      <IoCheckmarkCircle className="workout-detail-panel__header-sync-check" aria-hidden />
    </span>
  );
}


export interface WorkoutChromeSaveAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
}

export interface WorkoutChromeHeaderProps {
  title: string;
  titleContent?: React.ReactNode;
  sportEnglishName?: string | null;
  sportName?: string | null;
  dateText?: string;
  dateContent?: React.ReactNode;
  garminSynced?: boolean;
  garminSyncedLabel?: string;
  onClose?: () => void;
  closeLabel?: string;
  /** Form mode: interactive sport badge (picker button). */
  sportBadge?: React.ReactNode;
  /** View mode: icon action dock content. */
  dock?: React.ReactNode;
  /** Compact view mode: actions that sit in the identity capsule. */
  primaryActions?: React.ReactNode;
  /** Form mode: full-width save CTA in dock. */
  saveAction?: WorkoutChromeSaveAction;
  /** Rendered below dock inside the same sticky chrome (e.g. segment tabs). */
  trailing?: React.ReactNode;
  className?: string;
}

export function WorkoutChromeHeader({
  title,
  titleContent,
  sportEnglishName,
  sportName,
  dateText,
  dateContent,
  garminSynced,
  garminSyncedLabel,
  onClose,
  closeLabel = 'Close',
  sportBadge,
  dock,
  primaryActions,
  saveAction,
  trailing,
  className,
}: WorkoutChromeHeaderProps) {
  const { i18n } = useTranslation();
  const [sc1, sc2] = sportGradient(sportEnglishName ?? sportName);
  const showMeta = !!(dateContent || dateText || sportBadge);
  const showSportIcon = !!sportName && !sportBadge;

  return (
    <header
      className={['workout-detail-panel__header', 'workout-chrome-header', className].filter(Boolean).join(' ')}
      style={{ '--wd-sport-accent': sc1 } as React.CSSProperties}
    >
      <div className="workout-detail-panel__header-glow" aria-hidden />

      <div className="workout-detail-panel__header-stack">
        <div className="workout-detail-panel__header-identity">
          <span
            className="workout-detail-panel__header-sport"
            style={{ background: `linear-gradient(180deg, ${sc1}, ${sc2})` }}
            aria-hidden
          />
          <div className="workout-detail-panel__header-identity-main" dir={i18n.dir()}>
            {showSportIcon ? (
              <span className="workout-detail-panel__header-sport-icon" role="img" aria-label={sportName ?? ''} title={sportName ?? ''}>
                <WorkoutTypeIcon name={sportEnglishName ?? sportName} size={44} />
              </span>
            ) : null}
          <div className="workout-detail-panel__header-identity-text">
            <div className="workout-detail-panel__header-title-row">
              {titleContent ?? <div className="workout-detail-panel__header-title">{title}</div>}
            </div>
            {showMeta ? (
              <div className="workout-detail-panel__header-meta-row">
                {dateContent ?? (dateText ? (
                  <span className="workout-detail-panel__header-meta-date ltr-island">{dateText}</span>
                ) : null)}
                {(dateContent || dateText) && (sportBadge || sportName) ? (
                  <span className="workout-detail-panel__header-meta-sep" aria-hidden>|</span>
                ) : null}
                {sportBadge}

              </div>
            ) : null}
          </div>
          </div>
          {(onClose || primaryActions || garminSynced) ? (
            <div className="workout-detail-panel__header-leading-actions">
              {onClose ? (
                <div className="workout-detail-panel__header-close">
                  <WorkoutChromeIconButton label={closeLabel} onClick={onClose} close>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </WorkoutChromeIconButton>
                </div>
              ) : null}
              {primaryActions ? (
                <div className="workout-detail-panel__header-primary-actions">
                  {primaryActions}
                </div>
              ) : null}
              {garminSynced ? <GarminSyncIndicator label={garminSyncedLabel ?? ''} /> : null}

            </div>
          ) : null}
        </div>

        {saveAction ? (
          <div className="workout-detail-panel__header-dock">
            <button
              type="button"
              className="workout-detail-panel__header-btn workout-detail-panel__header-btn--save"
              disabled={saveAction.disabled || saveAction.loading}
              onClick={saveAction.onClick}
            >
              {saveAction.loading ? (
                <NmLoader size="xs" inline decorative />
              ) : (
                saveAction.label
              )}
            </button>
          </div>
        ) : dock ? (
          <div className="workout-detail-panel__header-dock">{dock}</div>
        ) : null}

        {trailing}
      </div>
    </header>
  );
}
